import { MesfModal } from "@dexteel/mesf-core";
import {
  Box,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { GetApp as GetAppIcon } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../../controls/buttons/FooterModalButtons";
import { modalStyles } from "@/shared/styles/modalStyles";
import { Process } from "../../../models/lmf.models";
import { useLMFDataEntryActions } from "../../../hooks/useLMFDataEntryActions";
import { PlugType } from "../../tables/table-plug-metrics/hooks/usePlugMetricsTableData";
import DurationInput from "@/shared/data-entries/common/DurationInput";
import { mmSsToDecimalMinutes } from "@/utils/durationHelpers";
import { validateDurationAgainstProcess } from "../../../helpers/de-helper";

type Props = {
  showModal: "" | "EDIT";
  onHide: () => void;
  process: Process | undefined;
  plugType: PlugType;
};

type FormValues = {
  insideArgonVol: string;
  outsideArgonVol: string;
  time: string;
};

type PlugField =
  | "PlugInsideVol"
  | "PlugInsideTime"
  | "PlugOutsideVol"
  | "PlugOutsideTime";

const NUMERIC_PATTERN = /^\d+(\.\d+)?$/;

const numericRules = (fieldLabel: string) => ({
  required: `${fieldLabel} is required`,
  pattern: {
    value: NUMERIC_PATTERN,
    message: `${fieldLabel} must be a valid number`,
  },
  min: {
    value: 0,
    message: `${fieldLabel} must be greater than or equal to 0`,
  },
});

const formatValue = (value: number | null | undefined): string =>
  value != null ? value.toFixed(2) : "0.00";

export const PlugMetricModal = ({
  showModal,
  onHide,
  process,
  plugType,
}: Props) => {
  const isInside = plugType === "Inside Plug";
  const { savePlugMetrics, revertPlugFieldFromPLC } = useLMFDataEntryActions();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isRevertLoading, setIsRevertLoading] = useState<PlugField | null>(
    null,
  );
  const [timeValue, setTimeValue] = useState<string | null>(null);
  const [timeInitMinutes, setTimeInitMinutes] = useState<number>(0);

  const {
    handleSubmit,
    control,
    reset,
    setError,
    clearErrors,
    formState: { isDirty, dirtyFields, errors },
  } = useForm<FormValues>({});

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    if (!process?.ProcessId) return;

    const timeDecimalMinutes =
      timeValue !== null ? mmSsToDecimalMinutes(timeValue) : null;

    if (timeDecimalMinutes !== null) {
      const totalSeconds = Math.round(timeDecimalMinutes * 60);
      const validation = validateDurationAgainstProcess(
        totalSeconds,
        process,
        "Time",
      );
      if (validation !== true) {
        setError("time", { message: validation });
        return;
      }
    }

    setIsSubmitLoading(true);
    await savePlugMetrics({
      ProcessId: process.ProcessId,
      PlugInsideVol: isInside
        ? dirtyFields.insideArgonVol
          ? parseFloat(data.insideArgonVol) || null
          : process.PlugInsideVol
        : process.PlugInsideVol,
      PlugInsideTime: isInside
        ? timeValue !== null
          ? timeDecimalMinutes
          : process.PlugInsideTime
        : process.PlugInsideTime,
      PlugOutsideVol: !isInside
        ? dirtyFields.outsideArgonVol
          ? parseFloat(data.outsideArgonVol) || null
          : process.PlugOutsideVol
        : process.PlugOutsideVol,
      PlugOutsideTime: !isInside
        ? timeValue !== null
          ? timeDecimalMinutes
          : process.PlugOutsideTime
        : process.PlugOutsideTime,
    });
    setIsSubmitLoading(false);
    onHide();
  };

  const handleRevertField = async (field: PlugField) => {
    setIsRevertLoading(field);
    await revertPlugFieldFromPLC(field);
    setIsRevertLoading(null);
  };

  const isManualField = (field: PlugField): boolean => {
    if (!process) return false;
    const manualFlags: Record<PlugField, boolean> = {
      PlugInsideVol: process.IsPlugInsideVolManual,
      PlugInsideTime: process.IsPlugInsideTimeManual,
      PlugOutsideVol: process.IsPlugOutsideVolManual,
      PlugOutsideTime: process.IsPlugOutsideTimeManual,
    };
    return manualFlags[field];
  };

  useEffect(() => {
    if (showModal === "EDIT" && process) {
      setTimeValue(null);
      setTimeInitMinutes(
        isInside
          ? (process.PlugInsideTime ?? 0)
          : (process.PlugOutsideTime ?? 0),
      );
      reset(
        isInside
          ? { insideArgonVol: formatValue(process.PlugInsideVol) }
          : { outsideArgonVol: formatValue(process.PlugOutsideVol) },
      );
    }
  }, [showModal, process, reset, isInside]);

  const renderArgonVolField = (
    name: keyof FormValues,
    label: string,
    plugField: PlugField,
    fieldLabel: string,
  ) => (
    <Grid item xs={12} md={6}>
      <Box display="flex" alignItems="flex-start" gap={0.5}>
        <Controller
          name={name}
          control={control}
          rules={numericRules(fieldLabel)}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type="text"
              variant="outlined"
              fullWidth
              label={label}
              error={!!error}
              helperText={error?.message}
              inputProps={{
                inputMode: "decimal",
              }}
            />
          )}
        />
        {isManualField(plugField) ? (
          <Tooltip title="Download from PLC">
            <IconButton
              color="primary"
              onClick={() => handleRevertField(plugField)}
              disabled={isRevertLoading !== null}
              size="small"
              sx={{ mt: 1 }}
            >
              <GetAppIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Grid>
  );

  const renderTimeField = (plugField: PlugField) => (
    <Grid item xs={12} md={6}>
      <Box display="flex" alignItems="flex-start" gap={0.5}>
        <Box flex={1}>
          <DurationInput
            label="Time [mm:ss]"
            mode="mm:ss"
            initMinutes={timeInitMinutes}
            onTimeChange={(time) => {
              setTimeValue(time);
              clearErrors("time");
            }}
            disabled={isSubmitLoading}
          />
          {errors.time && (
            <FormHelperText error>{errors.time.message}</FormHelperText>
          )}
        </Box>
        {isManualField(plugField) ? (
          <Tooltip title="Download from PLC">
            <IconButton
              color="primary"
              onClick={() => handleRevertField(plugField)}
              disabled={isRevertLoading !== null}
              size="small"
              sx={{ mt: 1 }}
            >
              <GetAppIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Grid>
  );

  return (
    <MesfModal
      title={isInside ? "EDIT INSIDE PLUG" : "EDIT OUTSIDE PLUG"}
      open={showModal === "EDIT"}
      handleClose={() => onHide()}
      id="plug-metric-modal"
      maxWidth="sm"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <MesfModal.Content dividers sx={modalStyles.modalContent}>
          <Grid container spacing={2}>
            {isInside ? (
              <>
                {renderArgonVolField(
                  "insideArgonVol",
                  "Argon Vol",
                  "PlugInsideVol",
                  "Inside Argon Vol",
                )}
                {renderTimeField("PlugInsideTime")}
              </>
            ) : (
              <>
                {renderArgonVolField(
                  "outsideArgonVol",
                  "Argon Vol",
                  "PlugOutsideVol",
                  "Outside Argon Vol",
                )}
                {renderTimeField("PlugOutsideTime")}
              </>
            )}
          </Grid>
        </MesfModal.Content>
        <MesfModal.Actions sx={modalStyles.modalActions}>
          <FooterModalButtons
            isSubmitLoading={isSubmitLoading}
            onHide={onHide}
            disabled={!isDirty && timeValue === null}
          />
        </MesfModal.Actions>
      </form>
    </MesfModal>
  );
};
